var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { extractGoogleMapsAuthParams } from '../utils';
import { useEffect, useState } from 'react';
import { useServicesContext } from '../../../context';
import { useGoogleConsent } from '@oneaudi/two-click';
import { isBrowser } from '../../../hooks';
let scriptLoadedPromise;
const loadGoogleLibraries = (locationFilterConfig_mapsAuthQueryParams, logger) => {
    var _a;
    if ((_a = window.google) === null || _a === void 0 ? void 0 : _a.maps) {
        logger === null || logger === void 0 ? void 0 : logger.warn('Google Maps already loaded by another application');
        return Promise.resolve(true);
    }
    if (scriptLoadedPromise) {
        return scriptLoadedPromise;
    }
    const { googleMapsApiKey, googleMapsClient, googleMapsChannel } = extractGoogleMapsAuthParams(locationFilterConfig_mapsAuthQueryParams);
    scriptLoadedPromise = new Promise((resolve) => {
        const script = document.createElement('script');
        if (googleMapsApiKey) {
            script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&loading=async&libraries=places,marker&callback=vtpInitMap`;
        }
        else if (googleMapsClient && googleMapsChannel) {
            script.src = `https://maps.googleapis.com/maps/api/js?client=${googleMapsClient}&channel=${googleMapsChannel}&loading=async&libraries=places,marker&callback=vtpInitMap`;
        }
        window.vtpInitMap = function () {
            if (!window.google) {
                logger === null || logger === void 0 ? void 0 : logger.error('Maps library not loaded correctly after initMap!');
            }
            resolve(!!window.google);
        };
        if (window.document.querySelector(`script#vtpGMapScript`)) {
            logger === null || logger === void 0 ? void 0 : logger.warn('map scripts were already loaded');
            script.remove();
            resolve(true);
        }
        if (!script.src || script.src === '//javascript:;') {
            script.remove();
            logger === null || logger === void 0 ? void 0 : logger.warn('map scripts not allowed!');
            scriptLoadedPromise = null;
            resolve(false);
        }
        else {
            script.id = `vtpGMapScript`;
            script.async = true;
            script.defer = true;
            script.onerror = () => {
                logger === null || logger === void 0 ? void 0 : logger.error('Failed to load Google Maps script!');
                scriptLoadedPromise = null;
                resolve(false);
            };
            document.body.appendChild(script);
        }
    });
    return scriptLoadedPromise;
};
export const useGoogleMaps = () => {
    const { getFeatureServices } = useServicesContext();
    const { 'vtp-configuration-service': configurationService, 'audi-content-service': contentService, } = getFeatureServices();
    const [libraryLoaded, setLibraryLoaded] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const { locationFilterConfig_mapsAuthQueryParams } = contentService.getContent().fields;
    const configuration = configurationService === null || configurationService === void 0 ? void 0 : configurationService.getConfiguration();
    const [needConsent, setNeedConsent] = useState(getBooleanFromQueryParam('googleCookie') || (configuration === null || configuration === void 0 ? void 0 : configuration.enableGoogleCookieConsent));
    const [effectiveConsent, setEffectiveConsent] = useState(false);
    const consent = useGoogleConsent(false, true);
    useEffect(() => {
        var _a;
        const needConsentConfig = getBooleanFromQueryParam('googleCookie') ||
            ((_a = configurationService === null || configurationService === void 0 ? void 0 : configurationService.getConfiguration()) === null || _a === void 0 ? void 0 : _a.enableGoogleCookieConsent);
        setNeedConsent(needConsentConfig);
        const eConsent = !needConsentConfig || consent;
        setEffectiveConsent(eConsent);
        const loadLibrary = () => __awaiter(void 0, void 0, void 0, function* () {
            if (eConsent) {
                const loaded = yield loadGoogleLibraries(locationFilterConfig_mapsAuthQueryParams);
                setLibraryLoaded(loaded);
                if (!loaded)
                    setLoadFailed(true);
            }
        });
        if (!libraryLoaded && eConsent) {
            loadLibrary().then();
        }
    }, [configuration, consent, locationFilterConfig_mapsAuthQueryParams]);
    return { effectiveConsent, libraryLoaded, loadFailed, needConsent };
};
const getBooleanFromQueryParam = (key) => {
    const urlParams = isBrowser() && new URLSearchParams(window.location.search);
    const param = urlParams === null || urlParams === void 0 ? void 0 : urlParams.get(key);
    if (!param)
        return false;
    return param.toLowerCase() === 'true';
};
